import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useBrand } from '@/shared/brand/useBrand';
import { LegalFooter } from '@/shared/legal';
import { useI18n } from '@/shared/i18n/useT';
import { isLocale, type Locale } from '@/shared/i18n';
import { PageHeader } from '@/shared/components/PageHeader';
import { applyThemeColors } from '@/shared/theme/themeColors';
import { useSession, savePendingBooking } from '@/shared/auth';
import {
  BookingApiError,
  bookingErrorKey,
  createBooking,
  fetchPublicCalendar,
  icsUrl,
  isSlotConflict,
  manageUrl,
} from '@/client/booking/api';
import { IntakeFormFields, type IntakeAnswersMap } from '@/client/booking/IntakeFormFields';
import { ErrorNotice } from '@/client/booking/ErrorNotice';
import { EventSummary } from '@/client/booking/EventSummary';
import { SlotPicker } from '@/client/booking/SlotPicker';
import { useBookingT, type BookingMessageKey } from '@/client/booking/i18n';
import {
  isValidTimeZone,
  monthKeyInZone,
  monthKeyOf,
  visitorTimeZone,
  type DayKey,
  type MonthKey,
} from '@/client/booking/datetime';
import type {
  BookingRecord,
  PublicCalendar,
  PublicEventType,
} from '@/client/booking/types';

type Step = 'slot' | 'details' | 'confirmed';
type PageState = 'loading' | 'ready' | 'notFound' | 'error';

/**
 * procalendar.app/:slug and /:slug/:eventSlug — everything the end client sees.
 *
 * The slug list is one screen, the booking itself another; the second is a
 * two-pane layout on desktop that restacks at 375px. The chosen day and
 * timezone live in the query string, so a link a professional pastes into a
 * chat reopens on exactly the day they meant, in the reader's own zone.
 */
export function BookingPage() {
  const { slug, eventSlug } = useParams();
  const t = useBookingT();
  const { locale, setLocale, formatLocale } = useI18n();
  const brand = useBrand();
  const session = useSession();
  const [params, setParams] = useSearchParams();

  const [calendar, setCalendar] = useState<PublicCalendar | null>(null);
  const [state, setState] = useState<PageState>(slug ? 'loading' : 'ready');
  const [loadErrorKey, setLoadErrorKey] =
    useState<BookingMessageKey>('calendarUi.loadError');

  const [step, setStep] = useState<Step>('slot');
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [booking, setBooking] = useState<BookingRecord | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formErrorKey, setFormErrorKey] = useState<BookingMessageKey | null>(
    null,
  );
  const [refreshToken, setRefreshToken] = useState(0);
  const [copied, setCopied] = useState(false);

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [intakeAnswers, setIntakeAnswers] = useState<IntakeAnswersMap>({});
  const [showIntakeErrors, setShowIntakeErrors] = useState(false);

  // One key per attempt, kept across retries: a double click, or a retry after
  // a timeout, must never produce a second booking.
  const idempotencyKey = useRef<string>(crypto.randomUUID());

  const visitorZone = useMemo(() => visitorTimeZone(), []);

  /* ------------------------------------------------------------- loading */

  useEffect(() => {
    if (!slug) return;
    let active = true;
    setState('loading');
    void fetchPublicCalendar(slug, eventSlug)
      .then((data) => {
        if (!active) return;
        setCalendar(data);
        setState('ready');
      })
      .catch((error: unknown) => {
        if (!active) return;
        setCalendar(null);
        setLoadErrorKey(bookingErrorKey(error));
        setState(
          error instanceof BookingApiError && error.status === 404
            ? 'notFound'
            : 'error',
        );
      });
    return () => {
      active = false;
    };
  }, [eventSlug, slug]);

  /**
   * The booking page wears the professional's colours, not Procalendar's
   * defaults. The palette arrives on the calendar payload because an end client
   * is not a member of that organization and cannot read `grid_settings`; here
   * it is only injected and undone. The undo closure matters: `/:slug` and
   * `/:slug/:eventSlug` are different tenants' pages in the same SPA.
   */
  useEffect(() => {
    const colors = calendar?.theme ?? null;
    if (!colors) return;
    const restore = applyThemeColors(colors);
    return () => restore();
  }, [calendar]);

  const event: PublicEventType | null = useMemo(() => {
    if (!calendar) return null;
    if (eventSlug) {
      return (
        calendar.eventTypes.find((entry) => entry.slug === eventSlug) ??
        calendar.eventTypes[0] ??
        null
      );
    }
    return null;
  }, [calendar, eventSlug]);

  /* --------------------------------------------------------- url + state */

  const timeZone = useMemo(() => {
    const requested = params.get('tz');
    if (requested && isValidTimeZone(requested)) return requested;
    return visitorZone;
  }, [params, visitorZone]);

  const selectedDay: DayKey | null = useMemo(() => {
    const requested = params.get('date');
    return requested && /^\d{4}-\d{2}-\d{2}$/.test(requested) ? requested : null;
  }, [params]);

  const month: MonthKey = useMemo(() => {
    const requested = params.get('month');
    if (requested && /^\d{4}-\d{2}$/.test(requested)) return requested;
    if (selectedDay) return monthKeyOf(selectedDay);
    return monthKeyInZone(new Date(), timeZone);
  }, [params, selectedDay, timeZone]);

  const patchParams = useCallback(
    (patch: Record<string, string | null>) => {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          for (const [key, value] of Object.entries(patch)) {
            if (value === null) next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const setTimeZone = useCallback(
    (next: string) => {
      setSelectedSlot(null);
      patchParams({ tz: next, date: null });
    },
    [patchParams],
  );

  const setSelectedDay = useCallback(
    (day: DayKey | null) => {
      setSelectedSlot(null);
      patchParams({ date: day, ...(day ? { month: monthKeyOf(day) } : {}) });
    },
    [patchParams],
  );

  const setMonth = useCallback(
    (next: MonthKey) => patchParams({ month: next, date: null }),
    [patchParams],
  );

  /* ------------------------------------------------- locale, title, meta */

  useEffect(() => {
    if (!calendar) return;
    const requested = params.get('lang');
    const allowed = calendar.locales;
    const next: Locale =
      requested && isLocale(requested) && allowed.includes(requested)
        ? requested
        : allowed.includes(locale)
          ? locale
          : (allowed[0] ?? 'en');
    if (next !== locale) setLocale(next);
  }, [calendar, locale, params, setLocale]);

  useEffect(() => {
    if (!calendar) return;
    const title = event
      ? `${event.name} · ${calendar.page.title}`
      : `${calendar.page.title} · ${brand.name}`;
    document.title = title;
    const description = event?.description ?? calendar.page.bio ?? '';
    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = description;
  }, [brand.name, calendar, event]);

  /* ---------------------------------------------------------- formatting */

  // `dateStyle`/`timeStyle` cannot be combined with individual component
  // options (weekday, day, month, year, …) — ECMA-402 throws a TypeError at
  // construction, not just a validation warning.
  const slotFormat = useMemo(
    () =>
      new Intl.DateTimeFormat(formatLocale, {
        dateStyle: 'full',
        timeStyle: 'short',
        timeZone,
      }),
    [formatLocale, timeZone],
  );

  const accountRequired =
    calendar?.bookingMode === 'account' && session.status !== 'signedIn';

  /* --------------------------------------------------------------- submit */

  const submit = async (formEvent: FormEvent<HTMLFormElement>) => {
    formEvent.preventDefault();
    if (!calendar || !event || !selectedSlot || submitting) return;

    if (event.intakeQuestions && event.intakeQuestions.length > 0) {
      const hasMissing = event.intakeQuestions.some((q) => {
        if (!q.required) return false;
        const ans = intakeAnswers[q.id];
        if (!ans) return true;
        if (typeof ans.value === 'string') return ans.value.trim().length === 0;
        if (Array.isArray(ans.value)) return ans.value.length === 0;
        if (typeof ans.value === 'object' && 'url' in ans.value) return !ans.value.url;
        return true;
      });
      if (hasMissing) {
        setShowIntakeErrors(true);
        return;
      }
    }

    const answersPayload: Record<string, unknown> = {};
    for (const [qId, ans] of Object.entries(intakeAnswers)) {
      answersPayload[qId] = ans.value;
    }

    setSubmitting(true);
    setFormErrorKey(null);
    try {
      const created = await createBooking(
        {
          slug: calendar.page.slug,
          eventTypeId: event.id,
          startsAt: selectedSlot,
          fullName: fullName.trim(),
          email: email.trim(),
          phone: phone.trim(),
          notes: notes.trim(),
          locale,
          intakeAnswers: answersPayload,
        },
        idempotencyKey.current,
      );
      setBooking(created);
      setStep('confirmed');
    } catch (error: unknown) {
      if (isSlotConflict(error)) {
        // The slot went while the form was being filled. Say so, drop the
        // selection and pull fresh times rather than letting them retry into
        // the same wall.
        setSelectedSlot(null);
        setRefreshToken((value) => value + 1);
        setStep('slot');
        setFormErrorKey('publicBooking.error.slotTakenRefreshed');
      } else {
        setFormErrorKey(bookingErrorKey(error));
      }
      // A rejected attempt is a new attempt: a fresh key, or the retry would
      // be deduplicated against the failed one.
      idempotencyKey.current = crypto.randomUUID();
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------------------------------------------------------------- views */

  if (!slug) {
    return (
      <PageHeader
        title={t('client.book.title')}
        description={t('client.book.description')}
      />
    );
  }

  if (state === 'loading') {
    return (
      <p role="status" className="text-sm text-muted">
        {t('common.loading')}
      </p>
    );
  }

  if (state === 'notFound' || !calendar) {
    return (
      <div className="mx-auto max-w-xl">
        <PageHeader
          title={t('calendarUi.publicNotFound')}
          description={t('publicBooking.notFoundBody')}
        />
        {state === 'error' && <ErrorNotice message={t(loadErrorKey)} />}
      </div>
    );
  }

  /* -- /:slug — the list of services ------------------------------------- */

  if (!event) {
    return (
      <div className="mx-auto max-w-2xl">
        <PageHeader
          title={calendar.page.title}
          {...(calendar.page.bio ? { description: calendar.page.bio } : {})}
        />
        <h2 className="mb-3 text-sm font-semibold text-fg">
          {t('publicBooking.chooseService')}
        </h2>
        {calendar.eventTypes.length === 0 ? (
          <p className="rounded-token border border-border bg-surface-2 p-4 text-sm text-muted">
            {t('publicBooking.noServices')}
          </p>
        ) : (
          <ul className="space-y-3">
            {calendar.eventTypes.map((entry) => (
              <li key={entry.id}>
                <Link
                  to={`/${encodeURIComponent(calendar.page.slug)}/${encodeURIComponent(entry.slug)}`}
                  className="tap-target flex items-center gap-3 rounded-token border border-border bg-surface-2 p-4 transition-colors hover:border-accent hover:bg-surface-3"
                >
                  <span
                    aria-hidden="true"
                    className="h-10 w-1 shrink-0 rounded-token bg-accent"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium text-fg">
                      {entry.name}
                    </span>
                    <span className="mt-0.5 block text-sm text-muted">
                      <EventMeta event={entry} />
                    </span>
                    {entry.description && (
                      <span className="mt-1 block text-sm text-muted">
                        {entry.description}
                      </span>
                    )}
                  </span>
                  <span aria-hidden="true" className="shrink-0 text-muted">
                    ›
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  /* -- confirmation ------------------------------------------------------ */

  if (step === 'confirmed' && booking) {
    const manage = manageUrl(booking);
    const manageHref = manage
      ? `${window.location.origin}${manage}`
      : null;
    return (
      <div className="mx-auto max-w-2xl">
        <PageHeader
          title={t('publicBooking.confirmed.title')}
          description={t('publicBooking.confirmed.body', { email })}
        />
        <div className="rounded-token border border-success bg-success-soft p-4 sm:p-5">
          <p className="text-base font-semibold text-fg">{event.name}</p>
          <p className="mt-1 text-sm text-fg">
            {slotFormat.format(new Date(booking.startsAt))}
          </p>
          <p className="mt-1 text-xs text-muted">
            {t('publicBooking.timezone.shownIn', { timezone: timeZone })}
          </p>
          <p className="mt-1 text-xs text-muted">
            {t('calendarUi.timezoneLabel', { timezone: calendar.page.timezone })}
          </p>
        </div>

        {booking.meetingUrl && (
          <div className="mt-4 rounded-xl border border-border bg-surface-2 p-4 text-center">
            <p className="text-xs text-muted mb-2">Video meeting link</p>
            <a
              href={booking.meetingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="tap-target inline-flex items-center justify-center rounded-full bg-accent px-5 py-2 text-sm font-semibold text-accent-fg hover:opacity-90"
            >
              Join Video Meeting
            </a>
          </div>
        )}

        {Boolean(booking.intakeAnswers && Object.keys(booking.intakeAnswers).length > 0) && (
          <div className="mt-4 rounded-xl border border-border bg-surface-2 p-3 text-left">
            <p className="text-xs font-semibold text-fg mb-1">Your answers:</p>
            <div className="space-y-1 text-xs text-muted">
              {Object.entries(booking.intakeAnswers!).map(([k, v]) => (
                <div key={k}>
                  <span className="font-medium text-fg">{k}:</span> {Array.isArray(v) ? v.join(', ') : String(v ?? '')}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <a
            href={icsUrl(booking.id, booking.manageToken)}
            className="tap-target flex items-center justify-center rounded-full bg-accent px-4 text-sm font-medium text-accent-fg"
          >
            {t('publicBooking.confirmed.addToCalendar')}
          </a>
          {manage && (
            <Link
              to={manage}
              className="tap-target flex items-center justify-center rounded-full border border-border-strong px-4 text-sm text-fg"
            >
              {t('publicBooking.confirmed.manage')}
            </Link>
          )}
          {manageHref && (
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard
                  ?.writeText(manageHref)
                  .then(() => setCopied(true))
                  .catch(() => setCopied(false));
              }}
              className="tap-target rounded-full border border-border px-4 text-sm text-fg"
            >
              {copied
                ? t('publicBooking.confirmed.copied')
                : t('publicBooking.confirmed.copyLink')}
            </button>
          )}
        </div>
        <p className="mt-3 text-xs text-muted">
          {t('publicBooking.confirmed.manageHint')}
        </p>

        <Link
          to={`/${encodeURIComponent(calendar.page.slug)}`}
          className="tap-target mt-5 inline-flex items-center text-sm text-link underline"
        >
          {t('publicBooking.confirmed.bookAnother')}
        </Link>
      </div>
    );
  }

  /* -- the booking screen ------------------------------------------------ */

  const when = selectedSlot ? slotFormat.format(new Date(selectedSlot)) : null;

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title={calendar.page.title}
        {...(calendar.page.bio ? { description: calendar.page.bio } : {})}
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(15rem,0.85fr)_minmax(0,1.15fr)]">
        <EventSummary
          event={event}
          professionalTitle={calendar.page.title}
          {...(when ? { when } : {})}
          timeZone={timeZone}
        />

        <section className="rounded-token border border-border bg-surface-2 p-3 sm:p-5">
          {formErrorKey && (
            <div className="mb-4">
              <ErrorNotice message={t(formErrorKey)} />
            </div>
          )}

          {accountRequired && (
            <div className="mb-4">
              <ErrorNotice
                tone="warning"
                message={t('publicBooking.error.accountRequired')}
                action={
                  <Link
                    to="/signin"
                    onClick={() =>
                      savePendingBooking({
                        slug: calendar.page.slug,
                        slots: selectedSlot ? [selectedSlot] : [],
                      })
                    }
                    className="tap-target inline-flex items-center rounded-token border border-border-strong px-3 text-sm"
                  >
                    {t('client.nav.account')}
                  </Link>
                }
              />
            </div>
          )}

          {step === 'slot' && (
            <>
              <h2 className="mb-3 text-base font-semibold text-fg">
                {t('publicBooking.chooseDate')}
              </h2>
              <SlotPicker
                slug={calendar.page.slug}
                eventTypeId={event.id}
                professionalZone={calendar.page.timezone}
                visitorZone={visitorZone}
                timeZone={timeZone}
                onTimeZoneChange={setTimeZone}
                month={month}
                onMonthChange={setMonth}
                selectedDay={selectedDay}
                onSelectDay={setSelectedDay}
                selectedSlot={selectedSlot}
                onSelectSlot={(slot) => {
                  setSelectedSlot(slot);
                  setFormErrorKey(null);
                  setStep('details');
                }}
                horizonDays={event.maxBookingDays}
                refreshToken={refreshToken}
              />
              <p className="mt-4">
                <Link
                  to={`/${encodeURIComponent(calendar.page.slug)}`}
                  className="text-sm text-muted underline"
                >
                  {t('publicBooking.backToServices')}
                </Link>
              </p>
            </>
          )}

          {step === 'details' && (
            <form onSubmit={(formEvent) => void submit(formEvent)} noValidate>
              <h2 className="text-base font-semibold text-fg">
                {t('publicBooking.yourDetails')}
              </h2>
              <p className="mt-1 text-sm text-accent">{when}</p>
              <p className="text-xs text-muted">
                {t('publicBooking.timezone.shownIn', { timezone: timeZone })}
              </p>
              <button
                type="button"
                onClick={() => setStep('slot')}
                className="tap-target mt-1 inline-flex items-center text-sm text-muted underline"
              >
                {t('publicBooking.changeTime')}
              </button>

              <div className="mt-4 space-y-3">
                <Field
                  label={t('calendarUi.fullName')}
                  value={fullName}
                  onChange={setFullName}
                  autoComplete="name"
                  required
                />
                <Field
                  label={t('calendarUi.email')}
                  value={email}
                  onChange={setEmail}
                  type="email"
                  autoComplete="email"
                  required
                />
                <Field
                  label={t('calendarUi.phone')}
                  value={phone}
                  onChange={setPhone}
                  type="tel"
                  autoComplete="tel"
                />
                <label className="block text-sm text-muted">
                  {t('publicBooking.notes')}
                  <textarea
                    value={notes}
                    onChange={(changeEvent) => setNotes(changeEvent.target.value)}
                    rows={3}
                    placeholder={t('publicBooking.notesPlaceholder')}
                    className="mt-1 w-full radius-box border border-border-strong bg-surface px-3 py-2 text-sm text-fg"
                  />
                </label>

                {event.intakeQuestions && event.intakeQuestions.length > 0 && (
                  <IntakeFormFields
                    questions={[...event.intakeQuestions]}
                    answers={intakeAnswers}
                    onChange={setIntakeAnswers}
                    showErrors={showIntakeErrors}
                  />
                )}
              </div>

              <div className="mt-5 flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={() => setStep('slot')}
                  className="tap-target rounded-full border border-border px-4 text-sm text-fg"
                >
                  {t('common.back')}
                </button>
                <button
                  type="submit"
                  disabled={submitting || accountRequired}
                  className="tap-target flex-1 rounded-full bg-accent px-4 text-sm font-medium text-accent-fg disabled:opacity-60"
                >
                  {submitting
                    ? t('publicBooking.confirming')
                    : t('calendarUi.confirmBooking')}
                </button>
              </div>
            </form>
          )}
        </section>
      </div>

      <LegalFooter />
    </div>
  );
}

/** Duration · price, the one line every service card repeats. */
function EventMeta({ event }: { event: PublicEventType }) {
  const t = useBookingT();
  const { formatCurrency } = useI18n();
  const price =
    event.price === null || event.price === 0
      ? t('publicBooking.free')
      : formatCurrency(event.price, event.currency);
  return (
    <>{`${event.durationMin} ${t('calendarUi.minutes')} · ${price}`}</>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  autoComplete?: string;
  required?: boolean;
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  autoComplete,
  required = false,
}: FieldProps) {
  return (
    <label className="block text-sm text-muted">
      {label}
      <input
        type={type}
        value={value}
        required={required}
        {...(autoComplete ? { autoComplete } : {})}
        onChange={(changeEvent) => onChange(changeEvent.target.value)}
        className="tap-target mt-1 w-full rounded-full border border-border-strong bg-surface px-3 text-sm text-fg"
      />
    </label>
  );
}
